import { formatEther, formatUnits } from "viem";

const BRIDGE_API_BASE_URL =
  "https://camp-bridge-production.up.railway.app/api/bridge";

export interface BridgeTransaction {
  id: string;
  status:
    | "PENDING_HOLD"
    | "READY_TO_RELEASE"
    | "BRIDGING"
    | "COMPLETED"
    | "FAILED";
  overallStatus:
    | "pending_hold"
    | "ready_to_release"
    | "bridging"
    | "completed"
    | "failed";
  amount: string;
  fromAddress: string;
  toAddress: string;
  timestamp: string;
  tokenSymbol: string;
  tokenType: "CAMP" | "ETH" | "USDC";
  sourceChain: string;
  destinationChain: string;
  timeRemaining?: number;
  isExpired?: boolean;
  isWhitelisted?: boolean;
  guid?: string;
  depositId?: string;
  campTransaction?: CampTransaction;
  ethereumTransaction?: EthereumTransaction;
}

interface CampTransaction {
  id: string;
  amount: string;
  timestamp: string;
  transactionHash: string;
  status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface EthereumTransaction {
  id: string;
  amount: string;
  timestamp: string;
  transactionHash: string;
  status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface UserStats {
  address: string;
  totalTransactions: number;
  totalVolume: string;
  isWhitelisted: boolean;
  campStats?: {
    pendingHoldCount: number;
    readyToReleaseCount: number;
    bridgingCount: number;
    completedCount: number;
  };
  ethereumStats?: {
    totalTransactions: number;
    totalVolume: string;
  };
}

export interface UserTransactionsResponse {
  success: boolean;
  data: {
    transactions: BridgeTransaction[];
    total: number;
    user: UserStats;
  };
  timestamp: string;
}

export interface TransactionStatusResponse {
  success: boolean;
  data: {
    transaction: BridgeTransaction;
    found: boolean;
  };
  timestamp: string;
}

export interface HealthResponse {
  success: boolean;
  data: {
    status: string;
    timestamp: string;
    services: {
      campSubgraph: string;
      ethereumSubgraph: string;
    };
  };
  timestamp: string;
}

// API Client Functions
export class BridgeAPIClient {
  private baseUrl: string;

  constructor(baseUrl: string = BRIDGE_API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error?.message || "API request failed");
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Bridge API Error: ${error.message}`);
      }
      throw new Error("Unknown error occurred while calling Bridge API");
    }
  }

  async getUserTransactions(
    address: string,
    options?: {
      limit?: number;
      offset?: number;
      status?: string;
    },
  ): Promise<UserTransactionsResponse> {
    const params = new URLSearchParams();

    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());
    if (options?.status) params.append("status", options.status);

    const query = params.toString() ? `?${params.toString()}` : "";

    return this.request<UserTransactionsResponse>(
      `/user/${address}/transactions${query}`,
    );
  }

  async getTransactionStatus(
    transactionId: string,
  ): Promise<TransactionStatusResponse> {
    return this.request<TransactionStatusResponse>(
      `/transaction/${transactionId}/status`,
    );
  }

  async getHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>("/health");
  }
}

// Create a default client instance
export const bridgeAPI = new BridgeAPIClient();

// Utility functions for processing API responses

// Map API status to UI status
export function mapApiStatusToUIStatus(
  apiStatus: string,
  overallStatus?: string,
):
  | "pending-hold"
  | "ready-for-execution"
  | "bridging"
  | "completed"
  | "failed" {
  // Use overallStatus as primary source if available
  const status = overallStatus || apiStatus;

  switch (status) {
    case "pending_hold":
    case "PENDING_HOLD":
      return "pending-hold";
    case "ready_to_release":
    case "READY_TO_RELEASE":
      return "ready-for-execution";
    case "bridging":
    case "BRIDGING":
      return "bridging";
    case "completed":
    case "COMPLETED":
      return "completed";
    case "failed":
    case "FAILED":
      return "failed";
    default:
      return "bridging"; // Default fallback
  }
}

// Get bridge direction from source and destination chains
export function getBridgeDirection(
  sourceChain: string,
  destinationChain: string,
): "camp-to-eth" | "eth-to-camp" {
  if (sourceChain === "camp" && destinationChain === "ethereum") {
    return "camp-to-eth";
  }
  if (sourceChain === "ethereum" && destinationChain === "camp") {
    return "eth-to-camp";
  }

  // Fallback based on common patterns
  if (sourceChain === "camp") {
    return "camp-to-eth";
  }
  return "eth-to-camp";
}

// Format token amount based on token type
export function formatTokenAmount(
  amount: string,
  tokenType: "CAMP" | "ETH" | "USDC",
): string {
  const amountBigInt = BigInt(amount);

  try {
    switch (tokenType) {
      case "USDC":
        // API should handle the decimal normalization
        // Try both 6 and 18 decimal formatting and use the appropriate one
        const result6 = formatUnits(amountBigInt, 6);
        const result18 = formatEther(amountBigInt);

        // If the 18-decimal result is a very small number (< 0.000001), use 6 decimals
        if (parseFloat(result18) < 0.000001 && parseFloat(result6) > 0) {
          return result6;
        }
        return result18; // Use 18 decimals for most cases

      case "ETH":
      case "CAMP":
      default:
        return formatEther(amountBigInt); // ETH and CAMP have 18 decimals
    }
  } catch (error) {
    console.error("Error formatting amount:", error);
    return amount; // Fallback to raw amount
  }
}

// Format timestamp from API (Unix timestamp as string)
export function formatTimestamp(timestamp: string): Date {
  return new Date(parseInt(timestamp) * 1000);
}

// Get explorer URL based on transaction hash and direction
export function getExplorerUrl(
  txHash: string,
  direction: "camp-to-eth" | "eth-to-camp",
  status?: string,
): string {
  // For completed transactions, txHash is from destination chain
  // For non-completed transactions, txHash is from origin chain
  const isCompleted = status === "completed" || status === "COMPLETED";

  if (direction === "camp-to-eth") {
    if (isCompleted) {
      // Completed: destination is Ethereum Mainnet
      return `https://etherscan.io/tx/${txHash}`;
    } else {
      // Non-completed: origin is Camp Network
      return `https://camp.cloud.blockscout.com/tx/${txHash}`;
    }
  } else {
    if (isCompleted) {
      // Completed: destination is Camp Network
      return `https://camp.cloud.blockscout.com/tx/${txHash}`;
    } else {
      // Non-completed: origin is Ethereum Mainnet
      return `https://etherscan.io/tx/${txHash}`;
    }
  }
}

export interface UserTransactionsResponse_Legacy {
  pendingHold: ProcessedTransaction_Legacy[];
  bridging: ProcessedTransaction_Legacy[];
  completed: ProcessedTransaction_Legacy[];
  total: number;
}

export interface ProcessedTransaction_Legacy {
  id: string;
  guid?: string;
  status:
    | "PENDING_HOLD"
    | "READY_TO_RELEASE"
    | "BRIDGING"
    | "COMPLETED"
    | "INITIATED"
    | "FAILED";
  sourceChain: "camp" | "ethereum";
  destinationChain: "camp" | "ethereum";
  amount: string;
  timestamp: string;
  transactionHash: string;
  unlockTimestamp?: string;
  fromAddress?: string;
  toAddress?: string;
  correlatedWith?: string;
  correlationMethod?: "guid" | "user-amount-time";
  user?: string;
  completedAt?: string;
  depositedAt?: string;
  srcEid?: string;
  dstEid?: string;
  depositId?: string;
  isReadyToExecute?: boolean;
  tokenSymbol?: string;
  tokenAddress?: string;
  tokenType?: "CAMP" | "ETH" | "USDC";
}

export function convertApiResponseToLegacy(
  apiResponse: UserTransactionsResponse,
): UserTransactionsResponse_Legacy {
  const transactions = apiResponse.data.transactions;

  const result: UserTransactionsResponse_Legacy = {
    pendingHold: [],
    bridging: [],
    completed: [],
    total: apiResponse.data.total,
  };

  transactions.forEach((tx) => {
    const legacyTx: ProcessedTransaction_Legacy = {
      id: tx.id,
      guid: tx.guid,
      status: tx.status,
      sourceChain: tx.sourceChain as "camp" | "ethereum",
      destinationChain: tx.destinationChain as "camp" | "ethereum",
      amount: tx.amount,
      timestamp: tx.timestamp,
      transactionHash:
        tx.campTransaction?.transactionHash ||
        tx.ethereumTransaction?.transactionHash ||
        tx.id,
      fromAddress: tx.fromAddress,
      toAddress: tx.toAddress,
      depositId: tx.depositId,
      tokenSymbol: tx.tokenSymbol,
      tokenType: tx.tokenType,
      isReadyToExecute: tx.status === "READY_TO_RELEASE",
    };

    switch (tx.overallStatus) {
      case "pending_hold":
      case "ready_to_release":
        result.pendingHold.push(legacyTx);
        break;
      case "bridging":
        result.bridging.push(legacyTx);
        break;
      case "completed":
        result.completed.push(legacyTx);
        break;
      default:
        result.bridging.push(legacyTx);
    }
  });

  return result;
}
